import {wrapAppWithDataFetcherProvider} from '@aboutyou/data-fetcher';
import {AB_TEST_COOKIES, getAbTestGroups} from '@aboutyou/feature-flags';
import {HeadContext} from '@aboutyou/head-elements';
import {capturePreHydrationSnapshot} from '@aboutyou/lazy-imports';
import {Log} from '@aboutyou/mlm';
import {getCookieDomainFromHostname, parseCookies} from '@aboutyou/storage';
import {UIProvider} from '@aboutyou/ui-components';
import {SERVER_RENDERING_MODE} from '@aboutyou/utils/server-rendering-modes';
import type {Request} from 'express';
import React from 'react';
import {createRoot, hydrateRoot} from 'react-dom/client';
import {IconsCache} from 'react-lazy-svg';
import {ensurePolyfillsLoaded} from './polyfills/ensurePolyfillsLoaded';
import {BaseProvider} from './providers/BaseProvider';
import {loadClientIconSVGLoader} from './providers/loadClientIconSVGLoader';
import {
  registerServiceWorker,
  unregisterAllServiceWorker,
} from './serviceWorker/registerServiceWorker';
// eslint-disable-next-line no-restricted-imports
import {getStaticConfig} from './staticConfig/getStaticConfig';
import {initializeStaticConfig} from './staticConfig/initializeStaticConfig';
import './utils/collectUnhandledErrors';
import {getBrowserAuthToken} from './utils/getBrowserAuthToken';
import {
  getServerCookiesOnClient,
  initializeServerCookies,
} from './utils/getServerCookiesOnClient';
import {
  getServerRenderingModeOnClient,
  initializeServerRenderingMode,
} from './utils/getServerRenderingModeOnClient';
import {reportWebVitals} from './utils/performance-logging/reportWebVitals';

interface StartConfig {
  components: {
    LegacyRootProvider?: React.ComponentType<
      React.PropsWithChildren<{serverRequest?: Request}>
    >;
    App: React.ComponentType<React.PropsWithChildren>;
  };
  serviceWorker?: boolean;
}

/**
 * The default buffer of 250 entries is full before a cold load finished
 * fetching its JS chunks, which would truncate the PageReadyTiming counts.
 */
const RESOURCE_TIMING_BUFFER_SIZE = 1000;

const startReactClientHydration = async (config: StartConfig) => {
  performance.setResourceTimingBufferSize?.(RESOURCE_TIMING_BUFFER_SIZE);

  try {
    await ensurePolyfillsLoaded();
  } catch (error) {
    // log the failure but try to continue, maybe the required polyfills are not required on this page anyhow (this will most probably fail)
    Log.error('@aboutyou/bootstrap', 'Failed to load required polyfills.', {
      error,
    });
  }

  const {LegacyRootProvider = React.Fragment, App} = config.components;
  const container = document.getElementById('react-root')!;

  await Promise.all([
    initializeStaticConfig(),
    initializeServerCookies(),
    initializeServerRenderingMode(),
  ]);

  const staticConfig = getStaticConfig();
  const {
    hostConfig,
    grpcConfig,
    i18nConfig,
    routesConfig,
    serverRenderingMode,
  } = staticConfig;

  const defaultCookieDomain = getCookieDomainFromHostname(
    staticConfig.hostConfig.hostname,
  );

  Log.setCustomAttribute('serverRenderingMode', serverRenderingMode);

  const headContext: HeadContext = {};

  const iconsCache: IconsCache = new Map();
  UIProvider.initClient(iconsCache);

  const loadIconSVG = loadClientIconSVGLoader({
    iconsCache,
    assetsUrl: hostConfig.assetsUrl,
  });

  const browserCookies = parseCookies({
    cookieString: document.cookie,
    whitelistedCookies: null,
  });

  /**
   * The html is cached at the edge and shared between users, and the Cloudflare worker leaves control groups out of
   * the cache key. The ab test groups the server rendered with (embedded server cookies, `grpcConfig.abTests`) can
   * therefore belong to another user, e.g. a visitor without AB testing consent gets a page rendered for a consented
   * control user. The groups of the current user only live in document.cookie: the worker sets it on every response
   * for users with AB testing consent, and never for anyone else.
   *
   * This does not cause a hydration mismatch: a variant that changes the markup of a page type is part of its cache
   * key, control renders the same markup as no group, and grpc request cache keys do not contain the ab test groups.
   */
  const browserAbTests = browserCookies[AB_TEST_COOKIES.Experiments];
  const serverCookies = {
    ...getServerCookiesOnClient(),
    [AB_TEST_COOKIES.Experiments]: browserAbTests,
  };
  grpcConfig.abTests = getAbTestGroups(browserAbTests);

  if (browserCookies[AB_TEST_COOKIES.UserId]) {
    grpcConfig.abTestIdentifier = browserCookies[AB_TEST_COOKIES.UserId];
  }

  const clientAuthToken = getBrowserAuthToken(defaultCookieDomain);

  const appContent = (
    <BaseProvider
      serverRenderingMode={getServerRenderingModeOnClient()}
      documentReferrer={document.referrer}
      storage={{
        serverCookies,
        browserCookies,
        defaultCookieDomain,
      }}
      headContext={headContext}
      loadIconSVG={loadIconSVG}
      knownIcons={iconsCache}
      hostConfig={hostConfig}
      i18nConfig={i18nConfig}
      grpcConfig={grpcConfig}
      featureFlagSetup={staticConfig.featureFlagConfig}
      routerConfig={{
        routesConfig,
        routerOptions: {
          initialLocation: {
            host: location.host,
            pathname: location.pathname,
            protocol: location.protocol?.replace(':', '') ?? 'http',
            search: location.search,
          },
        },
      }}
      authToken={clientAuthToken}
    >
      <LegacyRootProvider>
        <App />
      </LegacyRootProvider>
    </BaseProvider>
  );

  const {content, hasInitialState} = wrapAppWithDataFetcherProvider(
    appContent,
    hostConfig.environment === 'production',
  );
  const hasServerMarkupError = Boolean(
    document.querySelector('[data-tadarida-server-markup-error]'),
  );

  if (process.env.NODE_ENV === 'development') {
    import('./serverRenderingFailedAlert/ServerRenderingFailedAlert').then(
      mod => mod.renderServerRenderingFailedView(hasServerMarkupError),
    );
    // this setTimeout ensures that we also collect errors that occur after rendering
    typeof window !== 'undefined' &&
      import('./criticalErrorAlert/CriticalErrorAlert').then(mod => {
        window.setTimeout(() => {
          mod.renderCriticalErrorView();
        }, 1500);
      });
  }

  const ssrDisabled = serverRenderingMode === SERVER_RENDERING_MODE.CSR;

  const canHydrate = hasInitialState && !hasServerMarkupError && !ssrDisabled;

  if (!canHydrate && !ssrDisabled) {
    const logMessage =
      'Falling back to ReactDOM.render instead of ReactDOM.hydrate as no initial state is provided. This is considered an error and might have negative impact on performance. It is most probably an indicator that the server rendering did not work as expected.';

    try {
      Log.error('@aboutyou/bootstrap', logMessage);
    } catch (e) {
      console.error(logMessage);
    }
  }

  capturePreHydrationSnapshot({
    renderMode: canHydrate ? 'hydrate' : 'client-render',
    container,
  });

  const root = (() => {
    if (canHydrate) {
      return hydrateRoot(container, content);
    }

    const root = createRoot(container);
    root.render(content);
    return root;
  })();

  if (config.serviceWorker && process.env.NODE_ENV !== 'test') {
    registerServiceWorker({
      onUpdate() {
        // eslint-disable-next-line no-console
        console.log(`⚙️ Service worker has updated.`);
      },
    });
  } else {
    unregisterAllServiceWorker();
  }

  if (process.env.NODE_ENV !== 'test') {
    reportWebVitals();
  }

  return () => {
    root.unmount();
  };
};

export {startReactClientHydration};
